import { hexToRgb } from './colorUtils';
import {
  REFERENCE_VERSION,
  MIN_REGION_PIXELS,
  DEFAULT_EGG_TRANSFORM,
} from './constants';
import {
  TRANSPARENT_INDEX,
  findConnectedComponents,
  quantizeImageData,
} from './imageOps';

// Per-region pixel counts in each half of the bbox along the longer
// axis. Stored alongside pixelCount so scoring can apply a spatial
// "spread" check on large regions (drawing only the left half of a
// sweater shouldn't clear the threshold) with the right per-half
// threshold even when the mask is asymmetric. Without this stored
// info, scoring falls back to a 50/50 estimate which under-credits
// asymmetric masks.
function computeHalfPixels(pixels, bbox, canvasWidth) {
  const splitVertical = bbox.w >= bbox.h; // longer axis is x → split left/right
  const midX = bbox.x + bbox.w / 2;
  const midY = bbox.y + bbox.h / 2;
  let firstHalf = 0;
  let secondHalf = 0;
  for (let k = 0; k < pixels.length; k++) {
    const gi = pixels[k];
    const gx = gi % canvasWidth;
    const gy = (gi - gx) / canvasWidth;
    if (splitVertical) {
      if (gx < midX) firstHalf++;
      else secondHalf++;
    } else {
      if (gy < midY) firstHalf++;
      else secondHalf++;
    }
  }
  return {
    axis: splitVertical ? 'x' : 'y',
    counts: [firstHalf, secondHalf],
  };
}

function maskToPngDataUrl(pixels, bbox, canvasWidth) {
  const { x: ox, y: oy, w, h } = bbox;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const imgData = ctx.createImageData(w, h);
  const data = imgData.data;
  for (let k = 0; k < pixels.length; k++) {
    const gi = pixels[k];
    const gx = gi % canvasWidth;
    const gy = (gi - gx) / canvasWidth;
    const lx = gx - ox;
    const ly = gy - oy;
    const li = (ly * w + lx) * 4;
    data[li] = 255;
    data[li + 1] = 255;
    data[li + 2] = 255;
    data[li + 3] = 255;
  }
  ctx.putImageData(imgData, 0, 0);
  return canvas.toDataURL('image/png');
}

function quantizedImageDataUrl(indices, paletteRgb, width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;
  for (let i = 0; i < indices.length; i++) {
    const ci = indices[i];
    const di = i * 4;
    if (ci === TRANSPARENT_INDEX) {
      data[di + 3] = 0;
    } else {
      const p = paletteRgb[ci];
      data[di] = p.r;
      data[di + 1] = p.g;
      data[di + 2] = p.b;
      data[di + 3] = 255;
    }
  }
  ctx.putImageData(imgData, 0, 0);
  return canvas.toDataURL('image/png');
}

// Object-fit: contain algorithm. Returns the rect (in frame coords) where
// a natW×natH image actually lands inside a frameW×frameH box when rendered
// with object-fit: contain and centered. This is the image's "display box"
// *before* any CSS transform (scaleX/rotation/translate) is applied on top.
function containRect(frameW, frameH, natW, natH) {
  if (natW <= 0 || natH <= 0) {
    return { x: 0, y: 0, w: 0, h: 0 };
  }
  const imgAR = natW / natH;
  const frameAR = frameW / frameH;
  let w;
  let h;
  if (imgAR > frameAR) {
    w = frameW;
    h = frameW / imgAR;
  } else {
    h = frameH;
    w = frameH * imgAR;
  }
  return {
    x: (frameW - w) / 2,
    y: (frameH - h) / 2,
    w,
    h,
  };
}

function loadNaturalSize(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = src;
  });
}

// Rendering model that a consumer must reimplement to place the egg / marc
// pixels in canvas space at load time:
//
//   1. Start with a frame that matches canvas dimensions (canvas.width ×
//      canvas.height).
//   2. Render the source image centered in the frame with object-fit:
//      contain. That gives `displayBox` — a rect in canvas coordinates.
//   3. Apply the stored CSS transform around the frame's center:
//        egg:  translate(tx,ty) rotate(rotation) scale(scale) scaleX(scaleX)
//        marc: translate(tx,ty) rotate(rot) scale(scale)
//
// We save the parameters plus the computed `displayBox` for convenience so
// consumers don't have to reimplement object-fit contain.
export function buildReference({
  canvas,
  palette,
  marcId,
  eggId,
  marcTransform,
  eggTransform,
  eggLights,
  eggWarmth,
  eggRedshift,
  eggNaturalSize,
  marcNaturalSize,
}) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const paletteRgb = palette.map(hexToRgb);

  const { indices, width, height } = quantizeImageData(imgData, paletteRgb);
  const rawRegions = findConnectedComponents(indices, width, height);
  const regions = rawRegions
    .filter((r) => r.pixels.length >= MIN_REGION_PIXELS)
    // Sort by area descending so the bigger features (sweater, hair) land
    // first in the file. Makes relabel-pass UI more natural.
    .sort((a, b) => b.pixels.length - a.pixels.length)
    .map((r, i) => ({
      id: `r-${String(i + 1).padStart(3, '0')}`,
      colorIndex: r.colorIndex,
      color: palette[r.colorIndex],
      bbox: r.bbox,
      pixelCount: r.pixels.length,
      halfPixels: computeHalfPixels(r.pixels, r.bbox, canvas.width),
      mask: maskToPngDataUrl(r.pixels, r.bbox, canvas.width),
      label: '',
    }));

  const image = quantizedImageDataUrl(
    indices,
    paletteRgb,
    canvas.width,
    canvas.height
  );

  const eggDisplayBox = containRect(
    canvas.width,
    canvas.height,
    eggNaturalSize.width,
    eggNaturalSize.height
  );
  const marcDisplayBox = containRect(
    canvas.width,
    canvas.height,
    marcNaturalSize.width,
    marcNaturalSize.height
  );

  return {
    version: REFERENCE_VERSION,
    createdAt: new Date().toISOString(),
    canvas: { width: canvas.width, height: canvas.height },
    egg: {
      id: eggId,
      naturalSize: eggNaturalSize,
      displayBox: eggDisplayBox,
      transform: eggTransform ?? DEFAULT_EGG_TRANSFORM,
      lights: eggLights,
      warmth: eggWarmth,
      redshift: eggRedshift,
    },
    marc: {
      id: marcId,
      naturalSize: marcNaturalSize,
      displayBox: marcDisplayBox,
      transform: marcTransform,
    },
    palette,
    drawing: {
      image,
      regions,
    },
  };
}

// Builds the unlabeled reference JSON for a marc. Pure data-out: no side
// effects (no download, no storage write). Callers decide what to do —
// today author Save hands it off to label mode via sessionStorage; in the
// future we may also let an authorial export download it.
export default async function saveReference(opts) {
  const [eggNaturalSize, marcNaturalSize] = await Promise.all([
    loadNaturalSize(opts.eggSrc),
    loadNaturalSize(opts.marcSrc),
  ]);

  const reference = buildReference({
    ...opts,
    eggNaturalSize,
    marcNaturalSize,
  });
  const json = JSON.stringify(reference, null, 2);
  return {
    reference,
    json,
    regionCount: reference.drawing.regions.length,
    bytes: new Blob([json]).size,
  };
}
